import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { X, CreditCard, Check, Loader2, Film, Tv } from 'lucide-react';
import { initiateServicePayment, checkServicePayment, SERVICE_DETAILS } from '../utils/payment';

export function ConversionPaymentModal({ 
  conversionType, // 'screenplay' or 'tv_pilot'
  documentTitle,
  onClose, 
  onPaymentComplete 
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [paymentChecking, setPaymentChecking] = useState(true);
  const [hasPaid, setHasPaid] = useState(false);

  const serviceType = conversionType === 'screenplay' ? 'screenplay' : 'tv_pilot';
  const serviceDetails = SERVICE_DETAILS[serviceType];

  // Check if user has already paid
  useEffect(() => {
    async function checkPayment() {
      try {
        const result = await checkServicePayment(serviceType);
        setHasPaid(result.paid);
      } catch (err) {
        console.error('Error checking payment:', err);
      } finally {
        setPaymentChecking(false);
      }
    }
    checkPayment();
  }, [serviceType]);

  const handleProceedToPayment = async () => {
    setSubmitting(true);
    setError('');

    try {
      // Save context to sessionStorage
      sessionStorage.setItem('conversionType', conversionType);
      sessionStorage.setItem('conversionDocumentTitle', documentTitle);
      
      // Initiate Stripe checkout
      const { url } = await initiateServicePayment(serviceType, {
        document_title: documentTitle,
        conversion_type: conversionType
      });

      // Redirect to Stripe
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to initiate payment');
      setSubmitting(false);
    }
  };

  const handleStartConversion = () => {
    onPaymentComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="border-b">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              {conversionType === 'screenplay' ? (
                <Film className="w-8 h-8 text-purple-600" />
              ) : (
                <Tv className="w-8 h-8 text-blue-600" />
              )}
              <div>
                <CardTitle className="text-2xl">
                  {serviceDetails.name}
                </CardTitle>
                <CardDescription>
                  {documentTitle}
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* Service Description */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-3">What You Get:</h3>
            <p className="text-gray-700 mb-4">{serviceDetails.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-purple-600">
                ${serviceDetails.price}
              </span>
              <span className="text-sm text-gray-600">One-time payment</span>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-900">Includes:</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Complete AI-powered conversion in professional format</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Industry-standard formatting (Fountain compatible)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Production-ready quality for submissions</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Editable document you can customize further</span>
              </li>
            </ul>
          </div>

          {error && (
            <Alert className="bg-red-50 border-red-300">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {!hasPaid && !paymentChecking && (
            <Alert className="bg-blue-50 border-blue-300">
              <CreditCard className="w-4 h-4" />
              <AlertDescription className="text-blue-800">
                Secure payment powered by Stripe. You will be redirected to complete your purchase.
              </AlertDescription>
            </Alert>
          )}

          {hasPaid && (
            <Alert className="bg-green-50 border-green-300">
              <Check className="w-4 h-4" />
              <AlertDescription className="text-green-800">
                ✅ Payment confirmed! You can now start the conversion.
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={submitting || paymentChecking}
              className="flex-1"
            >
              Cancel
            </Button>
            
            {paymentChecking ? (
              <Button disabled className="flex-1 bg-purple-600">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Checking...
              </Button>
            ) : !hasPaid ? (
              <Button
                onClick={handleProceedToPayment}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Proceed to Payment (${serviceDetails.price})
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleStartConversion}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <Film className="w-4 h-4 mr-2" />
                Start Conversion
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
